import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';
import { calculateInstanceFinancials } from '../utils/earningsCalculations';

/**
 * Payment Batch Service
 *
 * Handles all operations for bulk payment tracking:
 * - Creating payment batches with transaction ID and proof
 * - Checking for duplicate transaction IDs
 * - Fetching batch details with linked jobs
 */
export const paymentBatchService = {
  /**
   * Check if a transaction reference already exists for the company
   * @param {string} companyId - Company UUID
   * @param {string} transactionRef - Transaction reference to check
   * @returns {Promise<boolean>} - True if exists, false if unique
   */
  async checkTransactionExists(companyId, transactionRef) {
    const { data, error } = await supabase
      ?.from('payment_batches')
      ?.select('id')
      ?.eq('company_id', companyId)
      ?.eq('transaction_ref', transactionRef)
      ?.maybeSingle();

    if (error) throw error;
    return !!data;
  },

  /**
   * Upload proof of payment image to storage
   * @param {File} file - The proof image file
   * @param {string} companyId - Company UUID for path organization
   * @returns {Promise<string>} - Storage path of uploaded file
   */
  async uploadProofImage(file, companyId) {
    const fileExt = file.name.split('.').pop();
    // Path within the bucket (don't include bucket name in path)
    const filePath = `${companyId}/${Date.now()}_${crypto.randomUUID()}.${fileExt}`;

    const { error } = await supabase
      ?.storage
      ?.from('payment-proofs')
      ?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;
    return filePath;
  },

  /**
   * Create a payment batch and link timesheets to it
   * @param {Object} params - Payment batch parameters
   * @param {string} params.companyId - Company UUID
   * @param {string} params.transactionRef - Bank transaction reference
   * @param {number} params.totalAmount - Total payment amount
   * @param {File} [params.proofFile] - Optional proof of payment image
   * @param {string[]} params.jobInstanceIds - Array of job instance IDs to mark as paid
   * @returns {Promise<Object>} - Created payment batch with linked timesheets
   */
  async createPaymentBatch({ companyId, transactionRef, totalAmount, proofFile, jobInstanceIds }) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Check for duplicate transaction ref first
    const exists = await this.checkTransactionExists(companyId, transactionRef);
    if (exists) {
      throw new Error('DUPLICATE_TRANSACTION');
    }

    // Upload proof image if provided
    let proofStoragePath = null;
    if (proofFile) {
      proofStoragePath = await this.uploadProofImage(proofFile, companyId);
    }

    // Create payment batch
    const { data: batch, error: batchError } = await supabase
      ?.from('payment_batches')
      ?.insert({
        company_id: companyId,
        transaction_ref: transactionRef,
        total_amount: totalAmount,
        proof_storage_path: proofStoragePath,
        created_by: user.id
      })
      ?.select()
      ?.single();

    if (batchError) {
      // Handle unique constraint violation at DB level
      if (batchError.code === '23505') {
        throw new Error('DUPLICATE_TRANSACTION');
      }
      throw batchError;
    }

    // Update job instances status to paid
    const { error: instanceError } = await supabase
      ?.from('job_instances')
      ?.update({
        status: 'paid',
        updated_at: new Date().toISOString()
      })
      ?.in('id', jobInstanceIds);

    if (instanceError) throw instanceError;

    // Update timesheets status to paid and link to batch
    const { error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.update({
        status: 'paid',
        payment_batch_id: batch.id
      })
      ?.in('job_instance_id', jobInstanceIds);

    if (timesheetError) throw timesheetError;

    // Send notifications to contractors
    try {
      console.log('[createPaymentBatch] Fetching timesheets for notifications, jobInstanceIds:', jobInstanceIds);

      // Get timesheet details with contractor and job info
      const { data: timesheets, error: fetchError } = await supabase
        ?.from('timesheets')
        ?.select(`
          id,
          contractor_id,
          total_hours,
          clock_in_time,
          clock_out_time,
          capped_hours,
          job_instance_id,
          job_instance:job_instances(
            id,
            overtime_request:overtime_requests(status, approved_overtime_amount),
            job:jobs(
              job_name,
              payment_type,
              payment_amount,
              estimated_hours
            )
          )
        `)
        ?.in('job_instance_id', jobInstanceIds);

      if (fetchError) {
        console.error('[createPaymentBatch] Error fetching timesheets for notifications:', fetchError);
      }

      console.log('[createPaymentBatch] Fetched timesheets for notifications:', timesheets?.length || 0, timesheets);

      // Send notification to each contractor
      for (const ts of (timesheets || [])) {
        console.log('[createPaymentBatch] Processing timesheet:', ts.id, 'contractor_id:', ts.contractor_id, 'job_instance:', ts.job_instance);

        if (!ts.contractor_id || !ts.job_instance) {
          console.log('[createPaymentBatch] Skipping - missing contractor_id or job_instance');
          continue;
        }

        // Calculate the contractor's earnings for this job using standard calculation
        const instanceForCalc = {
          timesheet: [ts],
          overtime_request: ts.job_instance?.overtime_request
        };
        const financials = calculateInstanceFinancials(instanceForCalc, ts.job_instance?.job);
        const jobAmount = (financials.payoutToContractor || 0) +
          (financials.overtimeApproved ? (financials.overtimePayoutToContractor || 0) : 0);

        console.log('[createPaymentBatch] Sending notification to contractor:', ts.contractor_id, 'for amount:', jobAmount);

        const notifyResult = await notificationService.notifyPaymentRecorded(
          ts.contractor_id,
          companyId,
          ts.job_instance.id,
          ts.job_instance.job?.job_name || 'Unknown Job',
          jobAmount,
          transactionRef
        );

        console.log('[createPaymentBatch] Notification sent, result:', notifyResult);
      }
    } catch (notifyError) {
      // Log but don't fail the payment if notifications fail
      console.error('Error sending payment notifications:', notifyError);
    }

    return {
      success: true,
      batch,
      count: jobInstanceIds.length
    };
  },

  /**
   * Get payment batch by ID with linked timesheets and job details
   * @param {string} batchId - Payment batch UUID
   * @returns {Promise<Object>} - Batch with linked jobs and contractor names
   */
  async getPaymentBatch(batchId) {
    // Fetch batch
    const { data: batch, error: batchError } = await supabase
      ?.from('payment_batches')
      ?.select(`
        *,
        created_by_user:user_profiles!payment_batches_created_by_fkey(
          id,
          full_name
        )
      `)
      ?.eq('id', batchId)
      ?.single();

    if (batchError) throw batchError;

    // Fetch linked timesheets with job and contractor details
    const { data: timesheets, error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.select(`
        id,
        amount_earned,
        job_instance:job_instances(
          id,
          scheduled_date,
          job:jobs(
            id,
            job_name,
            job_display_id
          )
        ),
        contractor:user_profiles!timesheets_contractor_id_fkey(
          id,
          full_name
        )
      `)
      ?.eq('payment_batch_id', batchId);

    if (timesheetError) throw timesheetError;

    // Get signed URL for proof image
    let proofUrl = null;
    if (batch.proof_storage_path) {
      const { data: urlData } = await supabase
        ?.storage
        ?.from('payment-proofs')
        ?.createSignedUrl(batch.proof_storage_path, 3600);
      proofUrl = urlData?.signedUrl;
    }

    return {
      ...batch,
      proof_url: proofUrl,
      timesheets: timesheets || []
    };
  },

  /**
   * Get payment batch by transaction reference for a company
   * @param {string} companyId - Company UUID
   * @param {string} transactionRef - Transaction reference to lookup
   * @returns {Promise<Object|null>} - Batch with linked jobs or null if not found
   */
  async getPaymentBatchByTransaction(companyId, transactionRef) {
    const { data: batch, error } = await supabase
      ?.from('payment_batches')
      ?.select('id')
      ?.eq('company_id', companyId)
      ?.eq('transaction_ref', transactionRef)
      ?.maybeSingle();

    if (error) throw error;
    if (!batch) return null;

    return this.getPaymentBatch(batch.id);
  },

  /**
   * Get payment batch ID for a specific timesheet
   * @param {string} timesheetId - Timesheet UUID
   * @returns {Promise<string|null>} - Batch ID or null if not in a batch
   */
  async getBatchIdForTimesheet(timesheetId) {
    const { data, error } = await supabase
      ?.from('timesheets')
      ?.select('payment_batch_id')
      ?.eq('id', timesheetId)
      ?.single();

    if (error) throw error;
    return data?.payment_batch_id || null;
  },

  /**
   * Get payment batch for a job instance (via its timesheet)
   * @param {string} jobInstanceId - Job instance UUID
   * @returns {Promise<Object|null>} - Batch with linked jobs or null
   */
  async getBatchForJobInstance(jobInstanceId) {
    const { data: timesheet, error } = await supabase
      ?.from('timesheets')
      ?.select('payment_batch_id')
      ?.eq('job_instance_id', jobInstanceId)
      ?.maybeSingle();

    if (error) throw error;
    if (!timesheet?.payment_batch_id) return null;

    return this.getPaymentBatch(timesheet.payment_batch_id);
  },

  /**
   * Search for job instances by transaction reference
   * Returns job instance IDs that were paid with the given transaction
   * @param {string} companyId - Company UUID
   * @param {string} transactionRef - Transaction reference to search
   * @returns {Promise<string[]>} - Array of job instance IDs
   */
  async searchByTransaction(companyId, transactionRef) {
    // First find the batch
    const { data: batch, error: batchError } = await supabase
      ?.from('payment_batches')
      ?.select('id')
      ?.eq('company_id', companyId)
      ?.ilike('transaction_ref', `%${transactionRef}%`)
      ?.maybeSingle();

    if (batchError) throw batchError;
    if (!batch) return [];

    // Get job instance IDs linked to this batch
    const { data: timesheets, error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.select('job_instance_id')
      ?.eq('payment_batch_id', batch.id);

    if (timesheetError) throw timesheetError;

    return timesheets?.map(t => t.job_instance_id) || [];
  },

  /**
   * Search for timesheets by transaction reference (for contractor view)
   * @param {string} contractorId - Contractor UUID
   * @param {string} transactionRef - Transaction reference to search
   * @returns {Promise<string[]>} - Array of timesheet IDs
   */
  async searchByTransactionForContractor(contractorId, transactionRef) {
    const { data, error } = await supabase
      ?.from('timesheets')
      ?.select(`
        id,
        payment_batch:payment_batches!timesheets_payment_batch_id_fkey(
          transaction_ref
        )
      `)
      ?.eq('contractor_id', contractorId)
      ?.not('payment_batch_id', 'is', null);

    if (error) throw error;

    // Filter by transaction ref (case-insensitive partial match)
    const searchLower = transactionRef.toLowerCase();
    return data
      ?.filter(t => t.payment_batch?.transaction_ref?.toLowerCase().includes(searchLower))
      ?.map(t => t.id) || [];
  },

  /**
   * Revert an entire payment batch
   * - Sets all linked timesheets back to 'approved' status
   * - Clears payment_batch_id and contractor confirmation fields
   * - Deletes the batch record
   * - Deletes proof image from storage (if exists)
   * @param {string} batchId - Payment batch UUID to revert
   * @returns {Promise<Object>} - Result with count of affected jobs
   */
  async revertPaymentBatch(batchId) {
    console.log('[revertPaymentBatch] Starting revert for batch:', batchId);

    // Get batch details first (for proof path and job instance IDs)
    const { data: batch, error: batchError } = await supabase
      ?.from('payment_batches')
      ?.select('proof_storage_path, transaction_ref')
      ?.eq('id', batchId)
      ?.single();

    if (batchError) {
      console.error('[revertPaymentBatch] Error fetching batch:', batchError);
      throw batchError;
    }

    console.log('[revertPaymentBatch] Found batch with transaction_ref:', batch?.transaction_ref);

    // Get all timesheets linked to this batch
    const { data: timesheets, error: tsError } = await supabase
      ?.from('timesheets')
      ?.select('id, job_instance_id')
      ?.eq('payment_batch_id', batchId);

    if (tsError) {
      console.error('[revertPaymentBatch] Error fetching timesheets:', tsError);
      throw tsError;
    }

    const jobInstanceIds = timesheets?.map(t => t.job_instance_id) || [];
    console.log('[revertPaymentBatch] Found', jobInstanceIds.length, 'timesheets to update');

    // Update timesheets: set status back to approved, clear batch link and confirmation
    const { error: tsUpdateError } = await supabase
      ?.from('timesheets')
      ?.update({
        status: 'approved',
        payment_batch_id: null,
        contractor_confirmed_received: false,
        contractor_confirmed_at: null
      })
      ?.eq('payment_batch_id', batchId);

    if (tsUpdateError) {
      console.error('[revertPaymentBatch] Error updating timesheets:', tsUpdateError);
      throw tsUpdateError;
    }

    console.log('[revertPaymentBatch] Timesheets updated successfully');

    // Update job instances: set status back to approved
    if (jobInstanceIds.length > 0) {
      const { error: jiError } = await supabase
        ?.from('job_instances')
        ?.update({
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        ?.in('id', jobInstanceIds);

      if (jiError) {
        console.error('[revertPaymentBatch] Error updating job instances:', jiError);
        throw jiError;
      }

      console.log('[revertPaymentBatch] Job instances updated successfully');
    }

    // Delete proof image from storage if exists
    if (batch.proof_storage_path) {
      console.log('[revertPaymentBatch] Deleting proof image:', batch.proof_storage_path);
      await supabase
        ?.storage
        ?.from('payment-proofs')
        ?.remove([batch.proof_storage_path]);
    }

    // Delete the batch record
    console.log('[revertPaymentBatch] Deleting batch record:', batchId);
    const { error: deleteError, count: deleteCount } = await supabase
      ?.from('payment_batches')
      ?.delete()
      ?.eq('id', batchId)
      ?.select();

    if (deleteError) {
      console.error('[revertPaymentBatch] Error deleting batch:', deleteError);
      throw deleteError;
    }

    console.log('[revertPaymentBatch] Delete response count:', deleteCount);

    // Verify the batch was actually deleted
    const { data: verifyBatch, error: verifyError } = await supabase
      ?.from('payment_batches')
      ?.select('id, transaction_ref')
      ?.eq('id', batchId)
      ?.maybeSingle();

    if (verifyError) {
      console.error('[revertPaymentBatch] Error verifying deletion:', verifyError);
    }

    if (verifyBatch) {
      console.error('[revertPaymentBatch] BATCH STILL EXISTS AFTER DELETE!', verifyBatch);
      throw new Error('Failed to delete payment batch - RLS policy may be blocking the operation');
    }

    console.log('[revertPaymentBatch] Batch deleted successfully. Transaction ref', batch?.transaction_ref, 'is now available for reuse.');

    return {
      success: true,
      revertedCount: jobInstanceIds.length
    };
  }
};

export default paymentBatchService;
